// Dimensions converter: a length / area / volume conversion utility.
// Takes 3 dimensions in a chosen input unit and converts the length, area
// (L × W) and volume (L × W × H) into common construction units. Pure math
// only — no shopping list, no affiliate.
import { round } from '../lib/format'

export type LengthUnit = 'in' | 'ft' | 'yd' | 'mm' | 'cm' | 'm'

export interface DimensionsInputs {
  length: number
  width: number
  height: number
  unit: LengthUnit
}

export interface DimensionsResult {
  // Length conversions (just the L value)
  lengthIn: number
  lengthFt: number
  lengthM: number
  lengthCm: number
  // Area = L × W
  areaSqIn: number
  areaSqFt: number
  areaSqYd: number
  areaSqM: number
  areaSqCm: number
  // Volume = L × W × H
  volCuIn: number
  volCuFt: number
  volCuYd: number
  volCuM: number
  volLiters: number
  volUsGallons: number
}

// Everything converts via meters as the base unit.
const TO_METERS: Record<LengthUnit, number> = {
  in: 0.0254,
  ft: 0.3048,
  yd: 0.9144,
  mm: 0.001,
  cm: 0.01,
  m: 1,
}

export const DEFAULT_DIMENSIONS: DimensionsInputs = {
  length: 10,
  width: 8,
  height: 4,
  unit: 'ft',
}

export function computeDimensions(i: DimensionsInputs): DimensionsResult {
  const k = TO_METERS[i.unit] ?? 1
  const Lm = Math.max(0, i.length) * k
  const Wm = Math.max(0, i.width) * k
  const Hm = Math.max(0, i.height) * k

  const areaM2 = Lm * Wm
  const volM3 = Lm * Wm * Hm

  // Length conversions
  const lengthIn = Lm / 0.0254
  const lengthFt = Lm / 0.3048
  const lengthM = Lm
  const lengthCm = Lm * 100

  // Area conversions
  const areaSqM = areaM2
  const areaSqCm = areaM2 * 10_000
  const areaSqIn = areaM2 / (0.0254 * 0.0254)
  const areaSqFt = areaM2 / (0.3048 * 0.3048)
  const areaSqYd = areaM2 / (0.9144 * 0.9144)

  // Volume conversions
  const volCuM = volM3
  const volCuIn = volM3 / Math.pow(0.0254, 3)
  const volCuFt = volM3 / Math.pow(0.3048, 3)
  const volCuYd = volM3 / Math.pow(0.9144, 3)
  const volLiters = volM3 * 1000
  const volUsGallons = volLiters / 3.785411784

  return {
    lengthIn: round(lengthIn, 3),
    lengthFt: round(lengthFt, 3),
    lengthM: round(lengthM, 4),
    lengthCm: round(lengthCm, 2),
    areaSqIn: round(areaSqIn, 2),
    areaSqFt: round(areaSqFt, 3),
    areaSqYd: round(areaSqYd, 3),
    areaSqM: round(areaSqM, 4),
    areaSqCm: round(areaSqCm, 1),
    volCuIn: round(volCuIn, 1),
    volCuFt: round(volCuFt, 3),
    volCuYd: round(volCuYd, 4),
    volCuM: round(volCuM, 5),
    volLiters: round(volLiters, 2),
    volUsGallons: round(volUsGallons, 2),
  }
}
